import React, { useState, useEffect, useCallback } from 'react'
import styles from './TOC.module.scss'
import { ReactComponent as ArrowSVG } from './ArrowSVG.svg'
import classNames from 'classnames'

export interface TOCProps {
  content: string
}

export const extractHeadings = (htmlString: string) => {
  if (typeof window === 'undefined') return []
  const parser = new DOMParser()
  const content = parser.parseFromString(htmlString, 'text/html')
  const headings: { id: string; title: string; index: number }[] = []

  content.querySelectorAll('ul li a').forEach((anchor, index) => {
    const id = anchor.getAttribute('href')?.substring(1) ?? ''
    const title = anchor.textContent ?? ''
    headings.push({ id, title, index })
  })

  return headings
}

export default function TOC({ content }: TOCProps) {
  const [isOpen, setIsOpen] = useState(true)
  const [activeId, setActiveId] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    if (window.innerWidth <= 1024) {
      setIsMobile(true)
    }
  }, [])

  const headings = extractHeadings(content)

  useEffect(() => {
    if (isMobile) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
            setActiveIndex(headings.find((heading) => heading.id === entry.target.id)?.index ?? 0)
          }
        })
      },
      {
        root: null, // Observe relative to the viewport
        rootMargin: '0px 0px -80% 0px',
        threshold: 0.9,
      }
    )

    headings.forEach((heading) => {
      const element = document.getElementById(heading.id)
      if (element) {
        observer.observe(element)
      }
    })

    return () => {
      headings.forEach((heading) => {
        const element = document.getElementById(heading.id)
        if (element) {
          observer.unobserve(element)
        }
      })
    }
  }, [headings, isMobile])

  const handleScroll = useCallback(() => {
    if (headings.length === 0) {
      return
    }

    if (activeIndex === headings.length - 1) {
      setProgress(100)
      return
    }

    const firstSection = document.getElementById(headings[activeIndex].id)
    const secondSection = document.getElementById(headings[activeIndex + 1].id)

    if (firstSection && secondSection) {
      const firstSectionTop = firstSection.getBoundingClientRect().top + window.scrollY
      const secondSectionTop = secondSection.getBoundingClientRect().top + window.scrollY
      const windowHeight = window.innerHeight
      const scrollPosition = window.scrollY + windowHeight
      if (scrollPosition > firstSectionTop && scrollPosition < secondSectionTop) {
        const progress = ((scrollPosition - firstSectionTop) / (secondSectionTop - firstSectionTop) / 2) * 100
        setProgress(progress)
      } else if (scrollPosition <= firstSectionTop) {
        setProgress(0)
      } else if (scrollPosition >= secondSectionTop) {
        setProgress(100)
      }
    }
  }, [headings, activeIndex])

  useEffect(() => {
    if (isMobile) return
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, isMobile])

  useEffect(() => {
    if (isMobile) return
    handleScroll()
  }, [handleScroll, isMobile])

  return (
    <div className={classNames(styles.root, { [styles.rootIsOpen]: isOpen })}>
      <header className={styles.header}>
        <h2 className={styles.title}>Contents</h2>
        <button
          className={styles.openToc}
          aria-label={isOpen ? 'Close table of contents' : 'Open table of contents'}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <>
              See less <ArrowSVG className={styles.arrow} />
            </>
          ) : (
            <>
              See more <ArrowSVG className={styles.closedArrow} />
            </>
          )}
        </button>
      </header>
      <div className={classNames(styles.content, { [styles.isOpen]: isOpen })}>
        <ul>
          {headings.map((heading) => (
            <li
              key={heading.id}
              className={classNames(styles.heading, {
                [styles.read]: activeIndex > heading.index,
                [styles.active]: activeId === heading.id,
              })}
            >
              <div
                className={classNames(styles.dotIndicator, {
                  [styles.read]: activeIndex > heading.index,
                  [styles.active]: activeId === heading.id,
                })}
              />
              <div className={styles.overflowLine} />
              <div
                className={classNames(styles.progress, {
                  [styles.read]: activeIndex > heading.index,
                  [styles.active]: activeId === heading.id,
                })}
                style={{ height: activeId === heading.id ? `${progress}%` : '0%' }}
              />
              <div className={styles.line} />
              <a href={`#${heading.id}`} className={activeId === heading.id ? styles.active : ''}>
                {heading.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
